import { Game } from './Game';
import { UI } from './UI';
import { MapId, KartId, ServerMessage, COUNTDOWN_SECS } from '@shared/types';

// ============================================================
// Bootstrap
// ============================================================

const container = document.getElementById('game-canvas')!;
const game = new Game(container);
const network = game.getNetwork();

let playerId = '';
let currentMapId: MapId = 'candy_world';
let raceStarted = false;

// Separate snapshot handler for the initial race setup.
// Once the first snapshot arrives after race_start we initialize the Game,
// then subsequent snapshots are handled internally by Game.ts.
let firstSnapshotHandler: ((msg: ServerMessage) => void) | null = null;

// ============================================================
// UI callbacks
// ============================================================

const ui = new UI({
  onSoloStart: (name: string, kartId: KartId, mapId: MapId) => {
    // Initialise audio early so countdown beeps work (this is after a user click)
    game.getSound().init();
    game.getSound().playMenuMusic();

    // Solo mode runs entirely client-side (no server needed)
    let remaining = COUNTDOWN_SECS;
    ui.showCountdown(remaining);
    game.getSound().playCountdownBeep(false);

    const countdownInterval = setInterval(() => {
      remaining--;
      if (remaining > 0) {
        ui.showCountdown(remaining);
        game.getSound().playCountdownBeep(false);
      } else {
        clearInterval(countdownInterval);
        ui.showCountdown(0); // "GO!"
        game.getSound().playCountdownBeep(true);
        setTimeout(() => {
          ui.showHUD();
          game.onLocalRaceFinish = (rankings) => {
            game.stop();
            raceStarted = false;
            ui.showResults(rankings);
            game.getSound().playMenuMusic();
          };
          game.startLocalRace(mapId, name, kartId);
          raceStarted = true;
        }, 600);
      }
    }, 1000);
  },

  onCreateRoom: async (name: string) => {
    try {
      await network.connect('create', name);
      // Server will respond with room_created, which we handle below
    } catch (err) {
      alert('Failed to connect to server.');
      console.error('[main] Connection error:', err);
    }
  },

  onJoinRoom: async (name: string, roomCode: string) => {
    try {
      await network.connect('join', name, { room: roomCode });
      // Server will respond with room_joined, which we handle below
    } catch (err) {
      alert('Failed to connect to server.');
      console.error('[main] Connection error:', err);
    }
  },

  onToggleReady: () => {
    network.send({ type: 'toggle_ready' });
  },

  onStartGame: () => {
    network.send({ type: 'start_game' });
  },

  onBackToMenu: () => {
    game.stop();
    network.close();
    raceStarted = false;
    firstSnapshotHandler = null;
    ui.showScreen('main-menu');
  },

  onPause: () => {
    game.pause();
  },

  onResume: () => {
    game.resume();
  },

  onVolumeChange: (volume: number) => {
    game.getSound().setVolume(volume);
  },
});

// Give the UI access to latency for lobby ping display
ui.setLatencyGetter(() => game.getLatency());

// ============================================================
// Server message handlers
// ============================================================

// These handlers are re-bound each time we connect, because
// network.close() clears all handlers. We bind them after
// each successful connect.

function bindNetworkHandlers(): void {
  // ── Room created (host) ──
  network.on('room_created', (msg: ServerMessage) => {
    if (msg.type !== 'room_created') return;
    playerId = msg.playerId;
    ui.showLobby(msg.roomCode);
  });

  // ── Room joined (non-host) ──
  network.on('room_joined', (msg: ServerMessage) => {
    if (msg.type !== 'room_joined') return;
    playerId = msg.playerId;
    ui.showLobby(msg.roomCode);
    ui.updateLobbyPlayers(msg.players, playerId);
  });

  // ── Lobby update ──
  network.on('lobby_update', (msg: ServerMessage) => {
    if (msg.type !== 'lobby_update') return;
    ui.updateLobbyPlayers(msg.players, playerId);
  });

  // ── Countdown ──
  network.on('countdown', (msg: ServerMessage) => {
    if (msg.type !== 'countdown') return;
    ui.showCountdown(msg.seconds);
    game.getSound().init(); // ensure audio context is ready (we are post-gesture)
    game.getSound().playMenuMusic();
    game.getSound().playCountdownBeep(msg.seconds === 0);
  });

  // ── Race start ──
  network.on('race_start', (msg: ServerMessage) => {
    if (msg.type !== 'race_start') return;

    // The server may or may not include mapId (it's in the type definition
    // but older server code may omit it). Fall back to our stored value.
    if (msg.mapId) {
      currentMapId = msg.mapId;
    }

    // Show HUD and wait for the first snapshot to initialize the race
    ui.showHUD();
    raceStarted = false;

    // Bind a one-shot snapshot listener to bootstrap the race
    firstSnapshotHandler = (snapMsg: ServerMessage) => {
      if (snapMsg.type !== 'snapshot') return;
      if (raceStarted) return;

      raceStarted = true;

      // Remove this one-shot handler (Game.ts will register its own)
      if (firstSnapshotHandler) {
        network.off('snapshot', firstSnapshotHandler);
        firstSnapshotHandler = null;
      }

      game.startRace(currentMapId, playerId, snapMsg.karts);
    };

    network.on('snapshot', firstSnapshotHandler);
  });

  // ── Race finish ──
  network.on('race_finish', (msg: ServerMessage) => {
    if (msg.type !== 'race_finish') return;
    game.stop();
    raceStarted = false;
    firstSnapshotHandler = null;
    ui.showResults(msg.rankings);
    game.getSound().playMenuMusic();
  });

  // ── Player left (during lobby or race) ──
  network.on('player_left', (msg: ServerMessage) => {
    if (msg.type !== 'player_left') return;
    // The lobby_update that follows will refresh the player list
    console.log(`[main] Player left: ${msg.playerId}`);
  });

  // ── Server error ──
  network.on('error', (msg: ServerMessage) => {
    if (msg.type !== 'error') return;
    console.error('[main] Server error:', msg.message);
    ui.showToast(msg.message);
  });

  // ── Disconnected ──
  network.on('_disconnected', (_msg: ServerMessage) => {
    // Only show alert if we were in a race or lobby (not on menu)
    const screen = ui.getCurrentScreen();
    if (screen === 'hud' || screen === 'lobby' || screen === 'countdown-overlay') {
      game.stop();
      raceStarted = false;
      firstSnapshotHandler = null;
      alert('Connection to server lost.');
      ui.showScreen('main-menu');
    }
  });

  // ── Item pickup ──
  network.on('item_pickup', (_msg: ServerMessage) => {
    game.getSound().playItemPickup();
  });

  // ── Player hit ──
  network.on('player_hit', (_msg: ServerMessage) => {
    game.getSound().playCollision();
  });

  // ── Lap complete ──
  network.on('lap_complete', (_msg: ServerMessage) => {
    game.getSound().playLapComplete();
  });
}

// ============================================================
// Monkey-patch network.connect to auto-bind handlers after connect
// ============================================================

const originalConnect = network.connect.bind(network);
network.connect = async (
  action: string,
  name: string,
  opts?: { room?: string; kartId?: KartId; mapId?: MapId }
): Promise<void> => {
  await originalConnect(action, name, opts);
  bindNetworkHandlers();
};

// ============================================================
// HUD update loop (runs always, only updates when in-race)
// ============================================================

let fpsFrames = 0;
let fpsLastTime = performance.now();
let fpsDisplay = 0;

function hudLoop(): void {
  requestAnimationFrame(hudLoop);

  // FPS counter (update once per second)
  fpsFrames++;
  const now = performance.now();
  if (now - fpsLastTime >= 1000) {
    fpsDisplay = fpsFrames;
    fpsFrames = 0;
    fpsLastTime = now;
  }

  if (ui.getCurrentScreen() !== 'hud') return;

  ui.updateFPS(fpsDisplay);
  const kart = game.getLocalKart();
  if (kart) {
    ui.updateHUD(kart);
    ui.updateDebug(game.getLatency(), 0);
    ui.updateLeaderboard(game.getAllKarts(), kart.id);
  }
}

hudLoop();

// ============================================================
// Handle invite links (?join=ROOMCODE)
// ============================================================

const urlParams = new URLSearchParams(location.search);
const joinCode = urlParams.get('join');
if (joinCode) {
  // Pre-fill room code and show the multiplayer join screen
  (document.getElementById('room-code-input') as HTMLInputElement).value = joinCode.toUpperCase();
  ui.showScreen('mp-menu');
  // Clean up the URL so refreshing doesn't re-trigger
  history.replaceState(null, '', location.pathname);
}
